import { Api, ApiRequestConfig, api } from 'api';
import { buildTrackingParams } from './build-tracking-params';

const NEED_LOG_FLAG = 'trackingLog';
const gaBasketAddEvent = 'GA:addToCart';
const trackingSearchEvent = 'tracking:search';

type GABasketEvent = CustomEvent<{ productId: string }>;
type RRSearchEvent = CustomEvent<{ phrase: string }>;

interface TrackingParams {
  productId?: string;
  stockId?: string;
  searchPhrase?: string;
  impressionContentId?: string;
}

export class TrackingHelper {
  private readonly api: Api;

  needLog: boolean;

  private orchestratorHost: string;

  private apiKey?: string;

  private requestID: string;

  private cookies: string;

  private readonly viewedContentIds = new Set<string>();

  private readonly clickedContentIds = new Set<string>();

  private readonly activeRequests = new Set<string>();

  constructor(apiInstance: Api) {
    this.api = apiInstance;
    this.needLog = __IS_SERVER__ ? false : Boolean(localStorage.getItem(NEED_LOG_FLAG));
  }

  setOrchestratorHostAndApiKey = (host: string, requestID: string, cookies: string, apiKey?: string) => {
    this.orchestratorHost = host;
    this.requestID = requestID;
    this.cookies = cookies;
    this.apiKey = apiKey;
  };

  log = (message: string) => {
    if (this.needLog) {
      // eslint-disable-next-line no-console
      console.log('message', message);
    }
  };

  private getDefaultConfig(): ApiRequestConfig {
    return {
      orchestratorHost: this.orchestratorHost,
      requestID: this.requestID,
      apiKey: this.apiKey,
      useCredentials: false,
    };
  }

  private async _sendRequest(endpoint: string, data: TrackingParams, useKeepalive = false): Promise<void> {
    const config = this.getDefaultConfig();
    const trackingParams = buildTrackingParams(data as Record<string, unknown>, config, this.cookies);
    const url = `${config.orchestratorHost}${endpoint}`;
    const requestKey = `${endpoint}-${data.impressionContentId || data.productId || 'unknown'}`;

    if (this.activeRequests.has(requestKey)) {
      return;
    }

    this.activeRequests.add(requestKey);

    try {
      if (useKeepalive) {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(config.apiKey ? { 'X-Api-Key': config.apiKey } : {}),
            ...(config.requestID ? { 'x-request-id': config.requestID } : {}),
          },
          body: JSON.stringify(trackingParams.data),
          keepalive: true,
          credentials: config.useCredentials ? 'include' : 'same-origin',
        });

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
      } else {
        await this.api.post(
          endpoint,
          trackingParams.data as unknown as Record<string, string>, // Двойное приведение
          config,
        );
      }
    } catch (error) {
      this.log(`Error in ${endpoint}`);
      throw error;
    } finally {
      this.activeRequests.delete(requestKey);
    }
  }

  getIsCookieAccepted = () => document.cookie.includes('cookie_accepted=true');

  dispatchSetAcceptedCookie = () => {
    document.dispatchEvent(new CustomEvent('rr:cookie_accepted'));
    this.log('cookie_accepted');
  };

  dispatchAddToCart = async (event: GABasketEvent, stockId: string) => {
    const { productId } = event.detail;
    this.log(`stockId=${stockId}, productId=${productId}`);

    await this._sendRequest('tracking/product/add-to-basket', { productId, stockId });
  };

  listenAddToCart = (stockId: string) => {
    document.addEventListener(
      gaBasketAddEvent,
      (event: Event) => this.dispatchAddToCart(event as GABasketEvent, stockId),
      { passive: true },
    );
  };

  dispatchSearchEvent = async (phrase: string) => {
    this.log(`tracking search phrase ${phrase}`);
    await this._sendRequest('tracking/search', { searchPhrase: phrase }, true);
  };

  listenSearch = () => {
    document.addEventListener(trackingSearchEvent, (event: Event) => {
      this.dispatchSearchEvent((event as RRSearchEvent).detail.phrase);
    });
  };

  dispatchContentClickedEvent = async (impressionContentId: string) => {
    if (this.clickedContentIds.has(impressionContentId)) return;

    await this._sendRequest('tracking/impression/click', { impressionContentId }, true);
    this.clickedContentIds.add(impressionContentId);
  };

  dispatchContentViewedEvent = async (impressionContentId: string) => {
    if (this.viewedContentIds.has(impressionContentId)) return;

    await this._sendRequest('tracking/impression/view', { impressionContentId });
    this.viewedContentIds.add(impressionContentId);
  };
}

export const trackingHelper = new TrackingHelper(api);

if (!__IS_SERVER__) {
  window.LMRR = window.LMRR || { rrHelper: trackingHelper };
}
